'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import {
  Search,
  X,
  Layers,
  Users,
  Truck,
  Receipt,
  Package,
  CheckSquare,
  Building2,
  ArrowRight,
  Clock,
} from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


// ─── Mock searchable data ────────────────────────────────────────────────────

interface SearchResult {
  id: string;
  group: string;
  label: string;
  sublabel?: string;
  href: string;
  badge?: string;
  badgeColor?: string;
  farm?: string;
}

const ALL_RESULTS: SearchResult[] = [
  // Flocks
  { id: 'sr-flock-001', group: 'Flocks', label: 'BR-2026-001 — Batch A1 Cobb 500', sublabel: 'Broiler · House A1 · 4,812 birds', href: '/flock-management', badge: 'Completed', badgeColor: 'var(--success)', farm: 'Idudi Poultry Farm' },
  { id: 'sr-flock-002', group: 'Flocks', label: 'BR-2026-002 — Batch A2 Ross 308', sublabel: 'Broiler · House A2 · 4,950 birds', href: '/flock-management', badge: 'Active', badgeColor: 'var(--primary)', farm: 'Idudi Poultry Farm' },
  { id: 'sr-flock-003', group: 'Flocks', label: 'LY-2026-003 — Layer Flock Isa Brown', sublabel: 'Layer · House B1 · 3,200 birds', href: '/flock-management', badge: 'Active', badgeColor: 'var(--primary)', farm: 'Idudi Poultry Farm' },
  { id: 'sr-flock-004', group: 'Flocks', label: 'BR-2026-004 — Kampala Batch K1', sublabel: 'Broiler · House K1 · 2,000 birds', href: '/flock-management', badge: 'Active', badgeColor: 'var(--primary)', farm: 'Kampala Demo Farm' },
  // Customers
  { id: 'sr-cust-001', group: 'Customers', label: 'Nakasero Market Traders', sublabel: 'Wholesale · Kampala · +256 700 123 456', href: '/customers', badge: 'Active', badgeColor: 'var(--success)', farm: 'Idudi Poultry Farm' },
  { id: 'sr-cust-002', group: 'Customers', label: 'Rolex Chicken Palace', sublabel: 'Retail · Jinja · +256 701 234 567', href: '/customers', badge: 'Active', badgeColor: 'var(--success)', farm: 'Idudi Poultry Farm' },
  { id: 'sr-cust-003', group: 'Customers', label: 'Kampala Supermarkets Ltd', sublabel: 'Wholesale · Kampala · +256 702 345 678', href: '/customers', badge: 'Active', badgeColor: 'var(--success)', farm: 'Kampala Demo Farm' },
  { id: 'sr-cust-004', group: 'Customers', label: 'Owino Market Vendors', sublabel: 'Retail · Kampala · +256 703 456 789', href: '/customers', badge: 'Inactive', badgeColor: 'var(--muted-foreground)', farm: 'Idudi Poultry Farm' },
  // Suppliers
  { id: 'sr-sup-001', group: 'Suppliers', label: 'Ugachick Hatchery', sublabel: 'Day-old Chicks · Kampala', href: '/suppliers', badge: 'Preferred', badgeColor: 'var(--primary)', farm: 'Idudi Poultry Farm' },
  { id: 'sr-sup-002', group: 'Suppliers', label: 'Ugachick Feeds Ltd', sublabel: 'Feed Supplier · Kampala', href: '/suppliers', badge: 'Active', badgeColor: 'var(--success)', farm: 'Idudi Poultry Farm' },
  { id: 'sr-sup-003', group: 'Suppliers', label: 'Agro Vet Supplies', sublabel: 'Veterinary Drugs · Jinja', href: '/suppliers', badge: 'Active', badgeColor: 'var(--success)', farm: 'Idudi Poultry Farm' },
  { id: 'sr-sup-004', group: 'Suppliers', label: 'Kampala Feeds & Nutrition', sublabel: 'Feed Supplier · Kampala', href: '/suppliers', badge: 'Active', badgeColor: 'var(--success)', farm: 'Kampala Demo Farm' },
  // Invoices
  { id: 'sr-inv-001', group: 'Invoices', label: 'INV-2026-0041 — Nakasero Market Traders', sublabel: 'UGX 2,400,000 · Due 10/09/2026', href: '/sales', badge: 'Unpaid', badgeColor: 'var(--warning)', farm: 'Idudi Poultry Farm' },
  { id: 'sr-inv-002', group: 'Invoices', label: 'INV-2026-0040 — Rolex Chicken Palace', sublabel: 'UGX 840,000 · Paid 01/09/2026', href: '/sales', badge: 'Paid', badgeColor: 'var(--success)', farm: 'Idudi Poultry Farm' },
  { id: 'sr-inv-003', group: 'Invoices', label: 'INV-2026-0039 — Kampala Supermarkets', sublabel: 'UGX 5,200,000 · Overdue', href: '/sales', badge: 'Overdue', badgeColor: 'var(--danger)', farm: 'Kampala Demo Farm' },
  { id: 'sr-inv-004', group: 'Invoices', label: 'INV-2026-0038 — Owino Market Vendors', sublabel: 'UGX 320,000 · Paid 28/08/2026', href: '/sales', badge: 'Paid', badgeColor: 'var(--success)', farm: 'Idudi Poultry Farm' },
  // Inventory
  { id: 'sr-item-001', group: 'Inventory', label: 'Broiler Starter Feed (Chick Mash)', sublabel: 'FD-001 · Feed Store A · 840 kg', href: '/inventory-stores', badge: 'Critical', badgeColor: 'var(--danger)', farm: 'Idudi Poultry Farm' },
  { id: 'sr-item-002', group: 'Inventory', label: 'Broiler Grower Feed', sublabel: 'FD-002 · Feed Store A · 3,240 kg', href: '/inventory-stores', badge: 'In Stock', badgeColor: 'var(--success)', farm: 'Idudi Poultry Farm' },
  { id: 'sr-item-003', group: 'Inventory', label: 'Newcastle Disease Vaccine (La Sota)', sublabel: 'VC-001 · Vaccine Store · 480 doses', href: '/inventory-stores', badge: 'Low', badgeColor: 'var(--warning)', farm: 'Idudi Poultry Farm' },
  { id: 'sr-item-004', group: 'Inventory', label: 'Poultry Drinkers (5L)', sublabel: 'EQ-001 · Equipment Store · 24 units', href: '/inventory-stores', badge: 'In Stock', badgeColor: 'var(--success)', farm: 'Kampala Demo Farm' },
  // Tasks
  { id: 'sr-task-001', group: 'Tasks', label: 'Vaccinate Flock LY-2026-003 — Newcastle', sublabel: 'Due today · Assigned: Apio Grace', href: '/tasks', badge: 'Overdue', badgeColor: 'var(--danger)', farm: 'Idudi Poultry Farm' },
  { id: 'sr-task-002', group: 'Tasks', label: 'Clean & disinfect House A1', sublabel: 'Due 06/09/2026 · Assigned: Okello James', href: '/tasks', badge: 'Pending', badgeColor: 'var(--warning)', farm: 'Idudi Poultry Farm' },
  { id: 'sr-task-003', group: 'Tasks', label: 'Submit weekly feed consumption report', sublabel: 'Due 07/09/2026 · Assigned: Namukasa Rita', href: '/tasks', badge: 'In Progress', badgeColor: 'var(--primary)', farm: 'Idudi Poultry Farm' },
  { id: 'sr-task-004', group: 'Tasks', label: 'Reorder Broiler Starter Feed', sublabel: 'Due 05/09/2026 · Assigned: Okello James', href: '/tasks', badge: 'Pending', badgeColor: 'var(--warning)', farm: 'Kampala Demo Farm' },
];

const GROUP_ICONS: Record<string, React.ElementType> = {
  Flocks: Layers,
  Customers: Users,
  Suppliers: Truck,
  Invoices: Receipt,
  Inventory: Package,
  Tasks: CheckSquare,
};

const RECENT_SEARCHES = ['BR-2026-002', 'Nakasero Market', 'Newcastle Vaccine', 'INV-2026-0041'];

interface GlobalSearchProps {
  open: boolean;
  onClose: () => void;
  selectedFarm: string;
}

export default function GlobalSearch({ open, onClose, selectedFarm }: GlobalSearchProps) {
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const router = useRouter();

  // Filter by farm scope + query
  const filtered = query.trim().length === 0
    ? []
    : ALL_RESULTS.filter((r) => {
        const farmMatch = selectedFarm === 'All Farms' || r.farm === selectedFarm;
        const q = query.toLowerCase();
        return farmMatch && (
          r.label.toLowerCase().includes(q) ||
          (r.sublabel?.toLowerCase().includes(q) ?? false)
        );
      });

  // Group results
  const grouped: Record<string, SearchResult[]> = {};
  filtered.forEach((r) => {
    if (!grouped[r.group]) grouped[r.group] = [];
    grouped[r.group].push(r);
  });

  // Flat list for keyboard nav
  const flatList = filtered;

  const navigate = useCallback((result: SearchResult) => {
    router.push(result.href);
    onClose();
    setQuery('');
  }, [router, onClose]);

  // Keyboard navigation
  useEffect(() => {
    if (!open) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') { onClose(); setQuery(''); }
      if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIndex((i) => Math.min(i + 1, flatList.length - 1)); }
      if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIndex((i) => Math.max(i - 1, 0)); }
      if (e.key === 'Enter' && activeIndex >= 0 && flatList[activeIndex]) {
        navigate(flatList[activeIndex]);
      }
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, flatList, activeIndex, navigate, onClose]);

  // Reset on open
  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIndex(-1);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Scroll active item into view
  useEffect(() => {
    if (activeIndex >= 0 && listRef.current) {
      const el = listRef.current.querySelector(`[data-idx="${activeIndex}"]`) as HTMLElement;
      el?.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex]);

  if (!open) return null;

  // Flat index tracker across groups
  let flatIdx = 0;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center pt-[10vh]"
      style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) { onClose(); setQuery(''); } }}
    >
      <div
        className="w-full max-w-xl mx-4 rounded-xl border shadow-2xl overflow-hidden"
        style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
      >
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b" style={{ borderColor: 'var(--border)' }}>
          <Search size={18} className="text-muted-foreground flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setActiveIndex(-1); }}
            placeholder="Search flocks, customers, suppliers, invoices, inventory, tasks…"
            className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none"
          />
          {query && (
            <button onClick={() => setQuery('')} className="text-muted-foreground hover:text-foreground transition-colors">
              <X size={16} />
            </button>
          )}
          <kbd className="hidden sm:flex items-center gap-0.5 px-1.5 py-0.5 text-xs rounded border text-muted-foreground flex-shrink-0" style={{ borderColor: 'var(--border)', fontSize: '0.65rem' }}>
            Esc
          </kbd>
        </div>

        {/* Farm scope badge */}
        <div className="flex items-center gap-2 px-4 py-2 border-b" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--muted)' }}>
          <Building2 size={12} style={{ color: 'var(--primary)' }} />
          <span className="text-xs text-muted-foreground">Scope:</span>
          <span className="text-xs font-medium text-foreground">{selectedFarm}</span>
        </div>

        {/* Results */}
        <div ref={listRef} className="max-h-[420px] overflow-y-auto scrollbar-thin">
          {query.trim() === '' ? (
            <div className="px-4 py-3">
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2" style={{ fontSize: '0.65rem', letterSpacing: '0.08em' }}>
                Recent Searches
              </div>
              {RECENT_SEARCHES.map((s) => (
                <button
                  key={s}
                  onClick={() => setQuery(s)}
                  className="flex items-center gap-2.5 w-full px-2 py-2 rounded-md text-sm text-foreground hover:bg-muted transition-colors text-left"
                >
                  <Clock size={13} className="text-muted-foreground flex-shrink-0" />
                  {s}
                </button>
              ))}
              <div className="mt-3 pt-3 border-t" style={{ borderColor: 'var(--border)' }}>
                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2" style={{ fontSize: '0.65rem', letterSpacing: '0.08em' }}>
                  Search Categories
                </div>
                <div className="grid grid-cols-3 gap-1.5">
                  {Object.entries(GROUP_ICONS).map(([group, Icon]) => (
                    <button
                      key={group}
                      onClick={() => setQuery(group.toLowerCase())}
                      className="flex items-center gap-2 px-2.5 py-2 rounded-md text-xs text-foreground hover:bg-muted transition-colors border"
                      style={{ borderColor: 'var(--border)' }}
                    >
                      <Icon size={13} style={{ color: 'var(--primary)' }} />
                      {group}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Search size={32} className="text-muted-foreground mb-3 opacity-40" />
              <p className="text-sm font-medium text-foreground">No results for &ldquo;{query}&rdquo;</p>
              <p className="text-xs text-muted-foreground mt-1">Try a different search term or change the farm scope</p>
            </div>
          ) : (
            <div className="py-2">
              {Object.entries(grouped).map(([group, items]) => {
                const GroupIcon = GROUP_ICONS[group] || Search;
                return (
                  <div key={group}>
                    <div className="flex items-center gap-2 px-4 py-1.5">
                      <GroupIcon size={12} style={{ color: 'var(--primary)' }} />
                      <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide" style={{ fontSize: '0.65rem', letterSpacing: '0.08em' }}>
                        {group}
                      </span>
                      <span className="text-xs text-muted-foreground">({items.length})</span>
                    </div>
                    {items.map((result) => {
                      const idx = flatIdx++;
                      const isActive = activeIndex === idx;
                      return (
                        <button
                          key={result.id}
                          data-idx={idx}
                          onClick={() => navigate(result)}
                          onMouseEnter={() => setActiveIndex(idx)}
                          className="flex items-center gap-3 w-full px-4 py-2.5 text-left transition-colors"
                          style={{ backgroundColor: isActive ? 'var(--muted)' : 'transparent' }}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-foreground truncate">{result.label}</div>
                            {result.sublabel && (
                              <div className="text-xs text-muted-foreground truncate mt-0.5">{result.sublabel}</div>
                            )}
                          </div>
                          {result.badge && (
                            <span
                              className="flex-shrink-0 text-xs px-2 py-0.5 rounded-full font-medium text-white"
                              style={{ backgroundColor: result.badgeColor, fontSize: '0.65rem' }}
                            >
                              {result.badge}
                            </span>
                          )}
                          {isActive && <ArrowRight size={14} className="flex-shrink-0 text-muted-foreground" />}
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-4 py-2 border-t" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--muted)' }}>
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <span className="flex items-center gap-1"><kbd className="px-1 py-0.5 rounded border text-xs" style={{ borderColor: 'var(--border)', fontSize: '0.6rem' }}>↑↓</kbd> Navigate</span>
            <span className="flex items-center gap-1"><kbd className="px-1 py-0.5 rounded border text-xs" style={{ borderColor: 'var(--border)', fontSize: '0.6rem' }}>↵</kbd> Open</span>
            <span className="flex items-center gap-1"><kbd className="px-1 py-0.5 rounded border text-xs" style={{ borderColor: 'var(--border)', fontSize: '0.6rem' }}>Esc</kbd> Close</span>
          </div>
          {filtered.length > 0 && (
            <span className="text-xs text-muted-foreground">{filtered.length} result{filtered.length !== 1 ? 's' : ''}</span>
          )}
        </div>
      </div>
    </div>
  );
}
